/*
 * Copyright (c) 2021 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory } from 'react-router-dom'
import { Box, Card, CardHeader, Typography, CardContent, Button } from '@material-ui/core'
import { logout } from '../services/AuthenticationService'

const useCustomStyles = makeStyles((theme) => ({
  accountHeader: {
    fontSize: '1.75rem',
    lineHeight: '20px',
    color: theme.palette.bluePrimary.main,
    fontFamily: theme.typography.fontFamily,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  boldText: {
    fontWeight: 'bold',
    paddingBottom: theme.spacing(0.5),
  },
  buttonBox: {
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(3),
    },
  },
  card: {
    [theme.breakpoints.down('xs')]: {
      height: '100%',
    },
  },
  cardContent: {
    textAlign: 'left',
    padding: theme.spacing(0.5, 4, 2.5),
  },
  cardHeader: {
    background: theme.palette.modalHeader,
    borderBottom: `1px solid ${theme.palette.modalGrayBorder}`,
    height: 82,
  },
  cardHolder: {
    width: 420,
    margin: 'auto',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      width: '100%',
      minWidth: 0,
      '& .MuiPaper-rounded': {
        borderRadius: 0,
      },
    },
  },
  successButton: {
    height: 44,
    width: 327,
    textTransform: 'none',
    [theme.breakpoints.down('xs')]: {
      width: 318,
    },
  },
}))

/**
 * The footer.
 * @returns {Node} - A react Node.
 */
function SuccessCard(props) {
  const { title, buttonText, boldBodyText, bodyText, isLogout = false } = props
  const classes = useCustomStyles()
  const history = useHistory()

  const goToLogin = () => {
    if (isLogout) {
      logout()
    }
    history.push('/')
  }

  const cardHeaderTitle = (
    <Typography variant='h1' data-testid='success-card-header' underline='none' className={classes.accountHeader}>
      {title}
    </Typography>
  )
  const cardText = (
    <div className={classes.cardContent}>
      <Typography variant='body2' className={classes.boldText}>
        {boldBodyText}
      </Typography>
      <Typography variant='body1'>{bodyText}</Typography>
    </div>
  )

  const successCTA = (
    <Box className={classes.buttonBox}>
      <Button
        color='primary'
        variant='contained'
        className={classes.successButton}
        data-testid='success-cta-button'
        onClick={goToLogin}>
        {buttonText}
      </Button>
    </Box>
  )

  return (
    <Box tabIndex={0} className={classes.cardHolder}>
      <Card elevation={9} className={classes.card}>
        <CardHeader className={classes.cardHeader} title={cardHeaderTitle} />
        <CardContent>
          {cardText}
          {successCTA}
        </CardContent>
      </Card>
    </Box>
  )
}

SuccessCard.propTypes = {
  title: PropTypes.string,
  buttonText: PropTypes.string,
  boldBodyText: PropTypes.string,
  bodyText: PropTypes.string,
  isLogout: PropTypes.bool,
}
export default SuccessCard
